import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { settingsChangesQueryKey } from '@/api/useCompanySettings';

export type AccountTerms = components['schemas']['AccountTermsResponse'];
export type AccountTermsSample = components['schemas']['AccountTermsSampleResponse'];
export type UpdateAccountTermsBody = components['schemas']['UpdateAccountTermsRequest'];

export const accountTermsQueryKey = ['account', 'terms'] as const;
export const accountTermsSampleQueryKey = ['account', 'terms', 'sample'] as const;

/** `AccountTermsVersion.TextMaxLength`: each box, measured as the API stores it (trimmed). */
export const TERMS_MAX_LENGTH = 10_000;

/** The three boxes, by the API's own field names. */
export const TERMS_FIELDS = ['estimateTerms', 'invoiceTerms', 'warrantyStatement'] as const;
export type TermsField = (typeof TERMS_FIELDS)[number];

/** The 400's keys, which are the request's property names in PascalCase. */
const API_FIELD: Record<string, TermsField> = {
  EstimateTerms: 'estimateTerms',
  InvoiceTerms: 'invoiceTerms',
  WarrantyStatement: 'warrantyStatement',
};

/** Every field the API refused, each with its sentence — the API collects all three at once. */
export class AccountTermsRefusedError extends Error {
  constructor(readonly fields: Partial<Record<TermsField, string>>) {
    super(Object.values(fields)[0] ?? 'The terms were refused.');
    this.name = 'AccountTermsRefusedError';
  }
}

/**
 * The shop's current terms (M2-48): what the next estimate or invoice sent
 * will carry. Explicit staleTime, so a test at the helper's 0 cannot hide a
 * missing cache update.
 */
export function useAccountTerms() {
  return useQuery({
    queryKey: accountTermsQueryKey,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/account/terms');
      if (!response.ok || !data)
        throw new Error(`GET /api/v1/account/terms failed: ${response.status}`);
      return data;
    },
    staleTime: 10 * 60_000,
  });
}

/**
 * Replaces the three boxes. The answer goes straight into the cache, and the
 * settings trail is re-read (a `Terms` row). Sent documents are NOT touched:
 * each keeps the version it went out with, which is the point.
 */
export function useUpdateAccountTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateAccountTermsBody) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/account/terms', { body });
      if (!response.ok || !data) {
        const errors = (error as { errors?: Record<string, string[]> } | undefined)?.errors ?? {};
        const fields: Partial<Record<TermsField, string>> = {};
        for (const [key, sentences] of Object.entries(errors)) {
          const field = API_FIELD[key];
          if (field && sentences[0]) fields[field] = sentences[0];
        }
        if (response.status === 400 && Object.keys(fields).length > 0)
          throw new AccountTermsRefusedError(fields);
        throw new Error(`PUT /api/v1/account/terms failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (terms) => {
      queryClient.setQueryData(accountTermsQueryKey, terms);
      void queryClient.invalidateQueries({ queryKey: settingsChangesQueryKey });
    },
  });
}

/** The sample wording, read only when someone asks for it ("Start from the sample", Kyle Q11-B). */
export function useFetchTermsSample() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.fetchQuery({
      queryKey: accountTermsSampleQueryKey,
      queryFn: async () => {
        const { data, response } = await apiClient.GET('/api/v1/account/terms/sample');
        if (!response.ok || !data)
          throw new Error(`GET /api/v1/account/terms/sample failed: ${response.status}`);
        return data;
      },
      staleTime: Infinity,
      retry: false,
    });
}
